import React, { useMemo } from "react";
import SearchableSelect from "./SearchableSelect";
import useBeneficiaryCategoryMap from "../hooks/useBeneficiaryCategoryMap";

/**
 * BeneficiaryCombobox — combobox spécialisé pour sélectionner un
 * bénéficiaire (contact) filtré par catégorie (Clientèle, Partenaire,
 * Fournisseur, Collaborateur…).
 *
 * Utilisation typique :
 *   <BeneficiaryCombobox
 *     categoryName="Clientèle"
 *     beneficiaries={clients}   // liste chargée par le parent
 *     brandId={form.brand_id}   // optionnel : filtrage département additionnel
 *     value={form.client_id}
 *     onChange={(v) => setForm({...form, client_id: v})}
 *     placeholder="Choisir un client"
 *     data-testid="tx-client-select"
 *   />
 *
 * Props principales :
 *  - beneficiaries : liste brute de bénéficiaires (chaque item doit avoir
 *                    id, denomination, category_id, brand_ids?)
 *  - categoryName  : nom de la catégorie autorisée (insensible casse). Si
 *                    omis, aucun filtrage catégorie n'est appliqué.
 *  - brandId       : si fourni, filtre supplémentaire sur brand_ids.
 *  - value, onChange, placeholder, searchPlaceholder, emptyLabel,
 *    allowClear, disabled, onCreateNew, createNewLabel : forwardés vers
 *    SearchableSelect.
 *  - data-testid   : posé sur le SearchableSelect (options : `-opt-{id}`).
 *
 * Le composant expose aussi la prop `noCategoryEmptyLabel` pour afficher
 * un message explicite quand aucune catégorie ne correspond côté serveur
 * (« Aucune catégorie "Partenaire" configurée »).
 */
export default function BeneficiaryCombobox({
  beneficiaries = [],
  categoryName,
  brandId,
  value,
  onChange,
  placeholder,
  searchPlaceholder = "Rechercher un contact…",
  emptyLabel,
  allowClear = false,
  disabled = false,
  onCreateNew,
  createNewLabel = "Nouveau contact",
  className,
  ...rest
}) {
  const { categories, getIdByName } = useBeneficiaryCategoryMap();
  const catId = categoryName ? getIdByName(categoryName) : null;

  const options = useMemo(() => {
    let list = Array.isArray(beneficiaries) ? beneficiaries : [];
    if (categoryName && catId) {
      list = list.filter((b) => b.category_id === catId);
    } else if (categoryName && !catId && categories.length > 0) {
      // La catégorie demandée est inconnue → liste vide plutôt que tout afficher.
      list = [];
    }
    if (brandId) {
      list = list.filter((b) => {
        const ids = b.brand_ids || (b.brand_id ? [b.brand_id] : []);
        return ids.includes(brandId);
      });
    }
    return list.map((b) => ({
      value: b.id,
      label: b.denomination || b.name || "—",
      keywords: `${b.denomination || ""} ${b.email || ""} ${b.phone || ""} ${b.phone_number || ""} ${b.city || ""}`,
      hint: b.email || b.city || undefined,
    }));
  }, [beneficiaries, categoryName, catId, categories.length, brandId]);

  const finalEmptyLabel =
    emptyLabel ||
    (categoryName && categories.length > 0 && !catId
      ? `Aucune catégorie « ${categoryName} » configurée`
      : options.length === 0 && categoryName
      ? `Aucun contact « ${categoryName} » disponible`
      : "Aucun résultat.");

  return (
    <SearchableSelect
      value={value || ""}
      onChange={onChange}
      options={options}
      placeholder={placeholder || "Sélectionner…"}
      searchPlaceholder={searchPlaceholder}
      emptyLabel={finalEmptyLabel}
      allowClear={allowClear}
      disabled={disabled}
      onCreateNew={onCreateNew}
      createNewLabel={createNewLabel}
      className={className}
      {...rest}
    />
  );
}
